"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { EASE } from "../../constants";

export type LeadSource = "facebook" | "mariana" | "form";

export type Lead = {
  id: string;
  firstName: string;
  name: string;
  src: string;
  source: LeadSource;
  optedIn: boolean;
};

// Leads stream in oldest-first; each runs the flow once, newest is prepended.
export const CAPTURE_LEADS: Lead[] = [
  {
    id: "maya",
    firstName: "Maya",
    name: "Maya Rivera",
    src: "/images/website/customers/maya-rivera.jpg",
    source: "facebook",
    optedIn: true,
  },
  {
    id: "devin",
    firstName: "Devin",
    name: "Devin Carter",
    src: "/images/website/customers/devin-carter.jpg",
    source: "mariana",
    optedIn: false,
  },
  // Third lead — two get the point across, but keep this around in case we want
  // to show a longer run later.
  // {
  //   id: "priya",
  //   firstName: "Priya",
  //   name: "Priya Nair",
  //   src: "/images/website/customers/priya-nair.jpg",
  //   source: "form",
  //   optedIn: true,
  // },
];

// Relative "added" time by position in the list — the top (newest) card reads
// "just now", and each card ages as newer leads push it down.
const ADDED_BY_POSITION = ["just now", "5 minutes ago", "12 minutes ago"];

function addedLabel(position: number) {
  return ADDED_BY_POSITION[position] ?? `${(position + 1) * 6} minutes ago`;
}

const SOURCE_BADGE: Record<LeadSource, { logo: string; label: string }> = {
  facebook: { logo: "/images/integrations/facebook/icon.png", label: "Paid ads" },
  mariana: { logo: "/images/integrations/mariana-tek/icon.png", label: "Mariana Tek" },
  form: { logo: "/images/icon.png", label: "Form" },
};

function SourceBadge({ source }: { source: LeadSource }) {
  const { logo, label } = SOURCE_BADGE[source];
  return (
    <Badge variant="outline" className="shrink-0 bg-background">
      <Image
        src={logo}
        width={50}
        height={50}
        alt=""
        className="mr-2 inline-block h-4 w-4 rounded-sm bg-background"
      />
      <span>{label}</span>
    </Badge>
  );
}

export function LeadCard({
  lead,
  active,
  position,
  reduce,
}: {
  lead: Lead;
  active: boolean;
  position: number;
  reduce: boolean;
}) {
  return (
    <motion.div
      layout
      initial={reduce ? false : { opacity: 0, y: -12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? undefined : { opacity: 0, scale: 0.98, transition: { duration: 0.2 } }}
      transition={{ duration: 0.45, ease: EASE }}
      className={cn(
        "flex items-start justify-between gap-2 rounded-lg border bg-background p-3 shadow-sm transition-colors",
        active ? "border-foreground/25" : "border-border",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Avatar name={lead.name} src={lead.src} size="sm" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{lead.name}</p>
          <p className="whitespace-nowrap text-xs text-muted-foreground">
            Added {addedLabel(position)}
          </p>
        </div>
      </div>
      <SourceBadge source={lead.source} />
    </motion.div>
  );
}
